import * as THREE from 'three';
import { fray, ringMail, roughOutline, sewn, strips, type ArmorKit } from './human-armors.js';
import { armhole } from './human-costumes.js';

/**
 * Everyday garments for the coast's people (`HUMAN_CLOTHING`), and the small pieces worn over them (`appearance.accessories`). Like the
 * plain clothes in `human-model.ts`, each is a few fitted layers over bare skin, so it suits either body: `cloth` is the garment,
 * its darker twin the trousers, hems and cuffs, and `accent` the shirt, chemise or collar under it. A garment reports how it is
 * built so the pieces worn over it clear its outermost layer.
 */
export interface Dressed {
  /** How far the outermost layer stands off the chest. */
  chest: number;
  /** The skirt or coat skirt, when there is one, so an apron hangs over it. */
  skirt?: { hem: number; flare: number; grow: number };
}
export const garmentLooks: Record<string, (k: ArmorKit) => Dressed> = {};
/** Each piece draws in `color` when given, else in its own role. */
export const accessoryLooks: Record<string, (k: ArmorKit, dressed: Dressed, color?: THREE.Material) => void> = {};

/** Where each long skirt hangs to, among the everyday garments; `legsCovered` turns it into the height above which the legs are hidden. */
const skirtHems: Record<string, (at: { ankle: number; knee: number; h: number }) => number> = {
  tunic: at => at.h - .22, dress: at => at.ankle + .12, 'short-dress': at => at.h - .2, 'shirt-skirt': at => at.h - .3,
  coat: at => at.knee - .04, robe: at => at.ankle + .05, kirtle: at => at.ankle + .045,
};
/** Above this height a garment's skirt hides the legs entirely, so neither skin nor hose is drawn there: a stride or a bent knee
 * could only push it through the cloth. Costs nothing per frame, and saves the triangles. */
export function legsCovered(style: string, at: { ankle: number; knee: number; h: number }) {
  const hem = skirtHems[style]?.(at);
  return hem === undefined ? undefined : hem + .07;
}

const parts = (k: ArmorKit) => ({ ...k.at, ...k.m });
const v3 = (x: number, y: number, z: number) => new THREE.Vector3(x, y, z);

/** Trousers to the boot and seat. */
function trousers(k: ArmorKit, material: THREE.Material, from = k.at.ankle + .06) { k.torsoLayer(k.at.crotch + .001, k.at.h + .1, .01, material); k.legLayer(from, k.at.h + .05, .008, material); }
/** Boots to `top` with a turned-down cuff. */
function boots(k: ArmorKit, top: number) { k.legLayer(k.at.ankle, top, .012, k.m.leather); k.legLayer(top - .04, top, .017, k.m.leather); }
/** A belt with a plain buckle. */
function belt(k: ArmorKit, y: number, grow: number, material: THREE.Material, width = .045) {
  k.torsoLayer(y - width / 2, y + width / 2, grow, material);
  k.plate(k.m.steel, .018, .006, k.onTorso(0, y, grow + .002));
}
/** Long sleeves with a darker cuff. */
function sleeves(k: ArmorKit, grow: number, material: THREE.Material, cuff: THREE.Material, from = k.at.wrist + .005) {
  k.armLayer(from, k.at.armTop, grow, material); k.armLayer(from, from + .045, grow + .005, cuff);
}
/** A row of buttons down the front. */
function buttons(k: ArmorKit, from: number, to: number, count: number, grow: number, material: THREE.Material) {
  for (let i = 0; i < count; i++) k.stud(material, k.onTorso(0, THREE.MathUtils.lerp(from, to, i / Math.max(1, count - 1)), grow), .007, .006);
}
/** The edge where a coat or jacket opens, a strip of what shows under it down the front from the collar to `to`. */
function opening(k: ArmorKit, to: number, grow: number, material: THREE.Material, skirt?: Dressed['skirt']) {
  k.ribbon(k.run([0, k.at.neckBase - .03], [0, Math.max(to, k.at.waist - .02)], grow, 1, 0, 10), .024, .003, material);
  if (skirt && to < k.at.waist - .02) {
    const path = Array.from({ length: 9 }, (_, i) => k.gownSpot(skirt.hem, skirt.flare, skirt.grow + .004, THREE.MathUtils.lerp(k.at.waist - .02, skirt.hem + .01, i / 8), 0));
    k.ribbon(path, .026, .003, material);
  }
}

/** A traveller's coat to the knee, open over a shirt and trousers, with a standing collar, a belt and boots. */
garmentLooks['coat'] = k => {
  const { h, waist, s, neckBase, knee, wrist, cloth, clothDark, accent, leather } = parts(k);
  // Trousers show only below the coat's skirt.
  k.legLayer(k.at.ankle + .06, legsCovered('coat', k.at)!, .008, clothDark); boots(k, knee - .08);
  k.torsoLayer(waist - .1, neckBase, .01, accent);
  const skirt = { hem: knee - .04, flare: .05, grow: .024 };
  k.torsoLayer(waist - .08, neckBase - .012, .02, cloth);
  k.gown(skirt.hem, skirt.flare, skirt.grow, cloth); k.gown(skirt.hem, skirt.flare, skirt.grow + .004, clothDark, skirt.hem, skirt.hem + .03);
  opening(k, skirt.hem, .022, clothDark, skirt);
  sleeves(k, .014, cloth, clothDark, wrist + .01);
  k.neckLoft(neckBase - .014, neckBase + .03, .026, cloth);
  belt(k, waist - .01, .03, leather);
  for (const y of [s - .07, s - .13]) k.stud(clothDark, k.onTorso(-.045, y, .023), .008, .006);
  return { chest: .03, skirt: { ...skirt, grow: skirt.grow + .004 } };
};

/** A short wool jacket to the hip over a loose shirt, trousers tucked into boots. */
garmentLooks['jacket'] = k => {
  const { h, waist, s, neckBase, knee, wrist, cloth, clothDark, accent } = parts(k);
  trousers(k, clothDark); boots(k, knee - .1);
  k.torsoLayer(h + .02, neckBase, .01, accent); k.skirt(h - .03, .012, .012, accent);
  k.torsoLayer(waist - .06, neckBase - .016, .02, cloth); k.skirt(h - .02, .02, .022, cloth); k.skirt(h - .02, .02, .026, clothDark);
  opening(k, h - .01, .022, accent);
  sleeves(k, .014, cloth, clothDark);
  for (const side of [-1, 1]) k.ribbon(k.run([side * .03, neckBase - .02], [side * .075, s - .13], .023, 1, side * .01, 8), .034, .004, clothDark);
  k.neckLoft(neckBase - .016, neckBase + .016, .024, cloth);
  return { chest: .028 };
};

/** A sleeveless waistcoat buttoned over a shirt with its sleeves rolled up, trousers and short boots. */
garmentLooks['waistcoat'] = k => {
  const { h, waist, s, neckBase, knee, elbow, armTop, cloth, clothDark, accent, wood } = parts(k);
  trousers(k, clothDark); boots(k, knee - .16);
  k.torsoLayer(h + .03, neckBase, .011, accent);
  k.armLayer(elbow - .03, armTop, .009, accent); k.armLayer(elbow - .04, elbow, .014, accent);
  k.neckLoft(neckBase - .008, neckBase + .016, .014, accent);
  k.bodyPanel(h + .02, s - .015, .02, cloth, [armhole(k, s - .015, .07)], true); k.torsoLayer(h + .02, h + .035, .024, clothDark);
  k.ribbon(k.run([0, s - .03], [0, h + .04], .021, 1, 0, 8), .012, .003, clothDark);
  buttons(k, s - .06, h + .06, 5, .022, wood);
  belt(k, h + .07, .026, k.m.leather, .04);
  return { chest: .026 };
};

/** A leather jerkin laced over a wool shirt, a short skirt of leather tabs, bracers, a belt and tall boots. */
garmentLooks['jerkin'] = k => {
  const { h, waist, s, neckBase, knee, wrist, elbow, cloth, clothDark, leather, dark, ivory } = parts(k);
  trousers(k, clothDark); boots(k, knee - .03);
  k.torsoLayer(h + .04, neckBase, .011, cloth); sleeves(k, .009, cloth, clothDark);
  k.torsoLayer(h - .02, s + .02, .021, leather);
  strips(k, { count: 8, top: h + .04, length: .14, flare: .12, width: .09, grow: .024, material: leather });
  for (let i = 0; i < 4; i++) { const y = s - .04 - i * .05; k.ribbon(k.run([-.025, y], [.025, y - .03], .023, 1, 0, 4), .007, .003, ivory); }
  k.armLayer(wrist + .02, elbow - .05, .016, leather);
  belt(k, h + .05, .027, dark, .05);
  return { chest: .028 };
};

/** A long robe or habit to the ankle with wide sleeves, a linen collar and a cord at the waist. Carries the wearer's mark if they have one. */
garmentLooks['robe'] = k => {
  const { h, waist, s, neckBase, ankle, wrist, armTop, cloth, clothDark, accent, leather } = parts(k);
  const skirt = { hem: ankle + .05, flare: .06, grow: .014 };
  k.torsoLayer(waist - .08, neckBase - .01, .012, cloth);
  k.gown(skirt.hem, skirt.flare, skirt.grow, cloth); k.gown(skirt.hem, skirt.flare, skirt.grow + .005, clothDark, skirt.hem, skirt.hem + .04);
  for (const side of [-1, 1]) {
    k.armPiece(side, wrist, armTop, .011, cloth);
    for (let i = 0; i < 3; i++) k.armPiece(side, wrist - .004 + i * .035, wrist + .04 + i * .035, .036 - i * .009, cloth);
    k.armPiece(side, wrist - .004, wrist + .008, .037, clothDark);
  }
  k.neckLoft(neckBase - .008, neckBase + .022, .018, accent);
  k.torsoLayer(waist - .012, waist + .012, .022, leather);
  const tail = k.loincloth({ top: waist, hem: h - .3, width: .012, dip: 0, flare: .03, ragged: 0 });
  tail(h - .3, waist, .03, leather);
  k.emblem({ x: 0, y: s - .14, size: .3, grow: .016 });
  return { chest: .02, skirt: { ...skirt, grow: skirt.grow + .005 } };
};

/** A fitted doublet with a standing linen collar and a row of buttons, a short skirt, breeches over hose and tall boots. */
garmentLooks['doublet'] = k => {
  const { h, waist, s, neckBase, knee, wrist, cloth, clothDark, accent, steel, leather } = parts(k);
  k.legLayer(k.at.ankle + .06, h + .05, .007, clothDark); k.torsoLayer(k.at.crotch + .001, h - .03, .012, clothDark);
  k.legLayer(knee + .02, h + .05, .02, clothDark); boots(k, knee + .01);
  k.torsoLayer(h - .03, neckBase - .01, .013, cloth); k.skirt(h - .07, .02, .016, cloth); k.skirt(h - .07, .02, .02, clothDark);
  k.neckLoft(neckBase - .01, neckBase + .032, .016, accent);
  sleeves(k, .011, cloth, accent);
  buttons(k, s - .05, waist + .02, 7, .016, steel);
  belt(k, waist - .02, .02, leather, .03);
  return { chest: .02 };
};

/** A working woman's kirtle: a laced bodice and a long skirt over a linen chemise whose sleeves show to the wrist. */
garmentLooks['kirtle'] = k => {
  const { h, waist, s, neckBase, ankle, wrist, armTop, cloth, clothDark, accent, leather } = parts(k);
  const skirt = { hem: ankle + .045, flare: .045, grow: .016 };
  k.torsoLayer(s - .06, neckBase, .01, accent); k.armLayer(wrist + .01, armTop, .009, accent);
  k.torsoLayer(waist - .06, s - .05, .016, cloth, true);
  k.gown(skirt.hem, skirt.flare, skirt.grow, cloth); k.gown(skirt.hem, skirt.flare, skirt.grow + .005, clothDark, skirt.hem, skirt.hem + .045);
  for (let i = 0; i < 4; i++) { const y = s - .07 - i * .04; k.ribbon(k.run([-.022, y], [.022, y - .025], .018, 1, 0, 4), .006, .003, leather); }
  k.legLayer(ankle, ankle + .08, .009, leather);
  return { chest: .02, skirt: { ...skirt, grow: skirt.grow + .005 } };
};

/** Leather armour over a mail shirt: a fighter out of uniform. The mail shows at the arms and below the jack. */
garmentLooks['mail-jack'] = k => {
  const { h, waist, s, neckBase, knee, wrist, elbow, armTop, clothDark, leather, dark } = parts(k);
  const mail = ringMail(k.mat('metal', .55, .6), 22), sleeve = ringMail(k.mat('metal', .55, .6), 8, 1.2);
  trousers(k, clothDark); boots(k, knee - .04);
  k.stack(h - .06, neckBase, .011, k.torsoLayer, mail, .03, .004); k.stack(elbow - .06, armTop, .011, k.armLayer, sleeve, .03, .004);
  k.torsoLayer(h + .03, s + .02, .022, leather);
  strips(k, { count: 10, top: h + .05, length: .12, flare: .1, width: .07, grow: .026, material: leather });
  for (let i = 0; i < 5; i++) k.stud(dark, k.onTorso(0, s - .05 - i * .05, .024), .007, .005);
  k.armLayer(wrist + .02, elbow - .06, .016, leather);
  belt(k, h + .06, .03, dark, .05);
  k.emblem({ x: .07, y: s - .07, size: .13, grow: .026 });
  return { chest: .03 };
};

/** A shawl round the shoulders, knotted at the breast. */
accessoryLooks['shawl'] = (k, dressed, color = k.m.accent) => {
  const { s, neckBase, shoulder, armTop } = k.at, grow = dressed.chest + .01;
  k.torsoLayer(s - .13, neckBase - .004, grow, color);
  for (const side of [-1, 1]) k.armPiece(side, shoulder - .1, armTop, .032, color, fray(shoulder - .1, .025, 6));
  k.stud(color, k.onTorso(0, s - .13, grow + .002), .018, .014);
};
/** A pilgrim's mantle: a short cape over the shoulders to the elbow, frayed at its edge. */
accessoryLooks['mantle'] = (k, dressed, color = k.m.cloth) => {
  const { s, neckBase, elbow, armTop } = k.at, grow = dressed.chest + .012;
  k.torsoLayer(s - .19, neckBase - .004, grow, color);
  for (const side of [-1, 1]) k.armPiece(side, elbow + .02, armTop, .036, color, fray(elbow + .02, .03, 7));
  k.neckLoft(neckBase - .01, neckBase + .02, grow + .006, color);
};
/** A cowl of rough cloth loose round the neck, the hood thrown back. */
accessoryLooks['cowl'] = (k, dressed, color = k.m.cloth) => {
  const { s, neckBase } = k.at, grow = dressed.chest + .012;
  k.torsoLayer(s - .07, neckBase - .004, grow, color);
  k.neckLoft(neckBase - .02, neckBase + .035, grow + .016, color);
};
/** An apron tied over the front, hanging over the skirt or coat when there is one. */
accessoryLooks['apron'] = (k, dressed, color = k.m.accent) => {
  const { waist, knee } = k.at, skirt = dressed.skirt ?? { hem: knee - .02, flare: .03, grow: .012 };
  const hem = Math.max(skirt.hem + .06, knee - .12), top = waist - .01, rows = 7, across = 5, half = .5, thick = .004;
  const row = (y: number) => {
    const outer = Array.from({ length: across }, (_, i) => { const spot = k.gownSpot(skirt.hem, skirt.flare, skirt.grow + .008, y, -half + 2 * half * i / (across - 1)); return spot.point.clone().addScaledVector(spot.normal, thick); });
    const inner = Array.from({ length: across }, (_, i) => k.gownSpot(skirt.hem, skirt.flare, skirt.grow + .008, y, half - 2 * half * i / (across - 1)).point);
    return [...outer, ...inner, outer[0]];
  };
  k.hang(Array.from({ length: rows }, (_, r) => row(THREE.MathUtils.lerp(top, hem, r / (rows - 1)))), color, skirt.grow + .008, v3(0, 0, 1),
    { key: `apron:${JSON.stringify(skirt)}`, columns: Array.from({ length: across }, (_, i) => across - 1 - i), rows: rows - 1 });
  k.torsoLayer(top - .012, top + .012, dressed.chest + .006, color);
  k.torsoLayer(top + .01, k.at.s - .06, dressed.chest + .004, color);
};
/** A strap from the left shoulder to the right hip, front and back: a map case's, a satchel's or a quiver's. */
accessoryLooks['strap'] = (k, dressed, color = k.m.leather) => {
  const { s, h } = k.at, grow = dressed.chest + .006;
  for (const face of [1, -1]) k.ribbon(k.run([-.11 * face, s + .01], [.11 * face, h + .1], grow, face, 0, 14), .03, .005, color);
};
/** A leather map case slung behind the right hip on a strap. */
accessoryLooks['map-case'] = (k, dressed, color = k.m.leather) => {
  accessoryLooks['strap'](k, dressed, color);
  const tube = k.tube(k.hips, color, .03, .03, .36, k.at.pw + .02, .05, -k.at.pd - .02, 10); tube.rotation.z = -.35;
  k.tube(k.hips, k.m.clothDark, .033, .033, .03, k.at.pw + .02 - Math.sin(.35) * .17, .05 + Math.cos(.35) * .17, -k.at.pd - .02, 10).rotation.z = -.35;
};
/** A satchel at the right hip on a strap across the body. */
accessoryLooks['satchel'] = (k, dressed, color = k.m.leather) => {
  accessoryLooks['strap'](k, dressed, color);
  const bag = k.box(k.hips, color, .05, .14, .18, k.at.pw + .045, .0, .01); bag.rotation.z = .08;
  k.box(k.hips, k.m.clothDark, .054, .05, .182, k.at.pw + .049, .05, .01).rotation.z = .08;
};
/** A leather yoke over both shoulders, as a carter wears to shift loads. */
accessoryLooks['yoke'] = (k, dressed, color = k.m.leather) => {
  const { s, neckBase, shoulder, armTop } = k.at, grow = dressed.chest + .008;
  k.torsoLayer(s - .05, neckBase - .012, grow, color);
  for (const side of [-1, 1]) {
    k.armPiece(side, shoulder - .07, armTop, .03, color);
    for (const face of [1, -1]) k.ribbon(k.run([side * .1, s - .04], [side * .05, k.at.waist + .02], grow - .002, face, 0, 8), .028, .005, color);
  }
};
/** A disc on a cord: a devotional token or a dull keepsake. */
accessoryLooks['pendant'] = (k, dressed, color = k.m.steel) => {
  const { s } = k.at, grow = dressed.chest + .003;
  const cord = Array.from({ length: 13 }, (_, i) => { const t = i / 12; return k.onTorso(THREE.MathUtils.lerp(-.065, .065, t), s + .01 - .1 * Math.sin(Math.PI * t), grow); });
  k.ribbon(cord, .005, .003, k.m.dark);
  k.plate(color, .019, .005, k.onTorso(0, s - .1, grow + .002));
};
/** A pouch on the belt at the left hip. */
accessoryLooks['pouch'] = (k, _dressed, color = k.m.leather) => { k.ellipsoid(k.hips, color, -(k.at.pw + .03), .03, .02, .034, .05, .03); };
/** Mended tears: rough patches sewn on with linen thread. */
accessoryLooks['patches'] = (k, dressed, color = k.m.clothDark) => {
  const { s, waist, h } = k.at, grow = dressed.chest - .004;
  sewn(k, { on: 'torso', at: [.06, s - .12], w: .06, h: .055, roll: .25, seed: 3, grow, material: color, thread: k.m.ivory });
  sewn(k, { on: 'torso', face: -1, at: [-.05, waist], w: .07, h: .06, roll: -.2, seed: 7, grow, material: color, thread: k.m.ivory });
  sewn(k, { on: 'torso', at: [-.07, h + .12], w: .05, h: .05, roll: .5, seed: 13, grow, material: color, thread: k.m.ivory });
};
/** Pale dust over the front, flour or ash. */
accessoryLooks['dust'] = (k, dressed, color = k.mat('ivory', 0, 1, 0xc3baa6)) => {
  const { s, waist, h } = k.at, grow = dressed.chest - .003;
  for (const [x, y, w, height, seed] of [[.05, s - .1, .08, .06, 1], [-.06, waist + .03, .07, .08, 2], [.02, h + .1, .1, .06, 3]]) {
    const at = [x, y] as const; k.pad({ on: 'torso', outline: roughOutline(at, w, height, seed, seed, .3), at, grow, depth: .0015, material: color });
  }
};
/** Smudges of soot and old burning. */
accessoryLooks['soot'] = (k, dressed, color = k.mat('dark', 0, 1, 0x2a2622)) => {
  const { s, waist } = k.at, grow = dressed.chest - .003;
  for (const [x, y, w, height, seed] of [[-.07, s - .08, .06, .07, 4], [.06, waist - .02, .07, .05, 5]]) {
    const at = [x, y] as const; k.pad({ on: 'torso', outline: roughOutline(at, w, height, seed, seed, .35), at, grow, depth: .0015, material: color });
  }
};
/** Mail glimpsed where a coat opens: a strip of rings down the front and round the collar. */
accessoryLooks['mail'] = (k, _dressed, color = k.mat('metal', .55, .6)) => {
  const mail = ringMail(color as THREE.MeshStandardMaterial, 6, 1.2);
  k.ribbon(k.run([0, k.at.neckBase - .03], [0, k.at.waist - .03], .0255, 1, 0, 10), .022, .002, mail);
  k.neckLoft(k.at.neckBase - .02, k.at.neckBase - .004, .015, mail);
};
